import { makeTheme } from 'dripsy';
import merge from 'lodash/fp/merge';
import { Platform } from 'react-native';

const theme = {
  colors: {
    background: 'white',
    chatBackground: 'white',
    text: '#0D0D0D',
    secondaryText: '#5E5E5E',
    tertiaryText: '#858585',
    badge: '#ED1C24',
    prmary: '#6210CC',
    accent: '#6210CC',
    border: '#D1D6DB',
    disabled: '#A6A6A6',
    announcementBarBackgroundDefault: '#C5EAD2',
    announcementBarBackgroundHover: '#79D1A2',
    announcementBarIcon: '#0D0D0D',
    announcementBarContent: '#0D0D0D',
    brandAvatarBackground: '#6210CC',
    brandAvatarIcon: '#FFF',
    activeTab: '#6210CC',
    inactiveTab: '#CCCCCC',
    primaryButtonBackgroundDefault: '#6210CC',
    primaryButtonBackgroundHover: '#4B11A1',
    primaryButtonContent: '#fff',
    secondaryButtonBackgroundDefault: '#E0E0E0',
    secondaryButtonBackgroundHover: '#CCCCCC',
    secondaryButtonContent: '#0D0D0D',
    disabledButtonBackground: '#E0E0E0',
    disabledButtonContent: '#858585',
    suggestedReplyBackgroundDefault: '#62EEAB',
    suggestedReplyBackgroundHover: '#39CF87',
    suggestedReplyText: '#0D0D0D',
    adminMessageBoxBackground: '#E1DBFF',
    csatSelectedItemBackground: '#E0E0E0',
    csatSelectedItemText: '#0D0D0D',
    incomingMessageBackground: '#F2F3F5',
    incomingMessageText: '#0D0D0D',
    outgoingMessageBackground: '#6210CC',
    outgoingMessageText: '#FFFFFF',
    messageTimestamp: '#858585',
    notificationCardBackground: '#F2F3F5',
    navigationHeaderBackground: 'white',
    navigationHeaderTextColor: '#0D0D0D',
    navigationTintColor: '#6210CC',
    listItemBackground: 'rgba(236, 236, 236, 0)',
    listItemBackgroundHover: '#F2F3F5',
    defaultFaqItemColor: '#6210CC',
    defaultFaqItemContent: '#FFF',
    online: '#259C72',
    avatarBackground: '#ECECEC',
    messageInputBackground: '#F2F3F5',
    dateLine: '#5E5E5E',
    sendFileButton: '#4E5968',
    readIconColor: '#259C72',
    animatingCircle: '#FFA756',
    messageInputBarBackground: 'white',
    carouselItemBackground: '#F2F4F6',
  },
  sizes: {
    channelListAvatar: 60,
  },
  space: {
    bubblePadding: 16,
    notificationCardSpacing: 16,
    suggestedReplyHorizontalSpacing: 16,
    suggestedReplyVerticalSpacing: 6,
    messageInputBarPaddingTop: 8,
  },
  radii: {
    bubble: 18,
    notificationCard: 8,
    suggestedReply: 16,
  },
  fontSizes: {
    xlarge: 22,
    large: 20,
    medium: 16,
    smedium: 14,
    small: 13,
    xsmall: 12,
    xxsmall: 11,
    xxxsmall: 10,
  },
  lineHeights: {
    large: (Platform.OS === 'android' ? 22 : undefined) as number,
    smedium: 20,
    medium: 20,
    small: 18,
    xsmall: 14,
    xsmall2: 16,
    xxsmall: 13,
    xxxsmall: 12,
  },
  styles: {
    textXxxsmall: {
      fontSize: 'xxxsmall',
      lineHeight: 'xxxsmall',
    },
    textXxsmall: {
      fontSize: 'xxsmall',
      lineHeight: 'xxsmall',
    },
    textXsmall: {
      fontSize: 'xsmall',
      lineHeight: 'xsmall',
    },
    textXsmall2: {
      fontSize: 'xsmall',
      lineHeight: 'xsmall2',
    },
    textSmall: {
      fontSize: 'small',
      lineHeight: 'small',
    },
    textSmedium: {
      fontSize: 'smedium',
      lineHeight: 'smedium',
    },
    textMedium: {
      fontSize: 'medium',
      lineHeight: 'medium',
    },
    textLarge: {
      fontSize: 'large',
      lineHeight: 'large',
    },
    buttonText: {
      fontWeight: '600',
    },
    channelListItemTitle: {
      variant: 'styles.textMedium',
      color: 'text',
      fontWeight: '600',
    },
    channelListItemMessage: {
      variant: 'styles.textSmall',
      color: 'tertiaryText',
    },
    channelListTimestamp: {
      color: '#7F7F7F',
      fontSize: 12,
    },
    notificationCardTimestamp: {
      color: '#A6A6A6',
      variant: 'styles.textXsmall',
      fontWeight: '500',
    },
    notificationCardTitle: {
      variant: 'styles.textMedium',
      fontWeight: '700',
      color: 'text',
    },
    notificationCardHeader: {
      variant: 'styles.textXsmall',
      fontWeight: '500',
    },
    messageText: {
      variant: 'styles.textSmedium',
    },
    messageTimestamp: {
      variant: 'styles.textXsmall',
      color: 'messageTimestamp',
    },
    messageNickname: {
      fontWeight: '700',
      color: 'secondaryText',
      fontSize: 'xsmall',
    },
    dateLine: {
      color: 'dateLine',
      variant: 'styles.textSmedium',
    },
    dateLineRule: {
      height: 1,
      backgroundColor: 'dateLine',
      opacity: 0,
    },
    suggestedReplyText: {
      fontWeight: '500',
      variant: 'styles.textSmedium',
    },
    csatQuestion: {
      fontSize: 'smedium',
      lineHeight: 'small',
      fontWeight: 'bold',
      marginBottom: 16,
    },
    faqItem: {
      borderRadius: 18,
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderWidth: 1,
      minWidth: 80,
      maxWidth: 250,
    },
    faqItemText: {
      variant: 'styles.textSmedium',
    },
    messageInput: {
      borderRadius: 20,
      py: 4,
      px: 16,
    },
    messageInputText: {
      variant: 'styles.textSmedium',
    },
    carouselItem: {
      borderRadius: 16,
      borderWidth: 1,
      borderColor: '#D9D9D9',
    },
    carouselItemButton: {
      borderTopWidth: '1',
      borderColor: 'rgba(0, 0, 0, 0.12)',
    },
    carouselItemWrapper: {},
    carouselItemButtonText: {
      fontSize: 16,
      fontWeight: '600',
    },
    carouselItemTitle: {
      fontSize: 12,
      my: 8,
      px: 10,
    },
    carouselItemPrice: {
      fontSize: 16,
      fontWeight: '600',
      px: 10,
    },
  },
  types: {
    reactNativeTypesOnly: true,
  },
};

export const defaultTheme = makeTheme(theme);

export const darkTheme = makeTheme(
  merge(defaultTheme, {
    colors: {
      background: '#161616',
      chatBackground: '#161616',
      messageInputBarBackground: '#161616',
      navigationHeaderBackground: '#2C2C2C',
      navigationHeaderTextColor: 'rgba(255, 255, 255, 0.88)',
      text: 'rgba(255, 255, 255, 0.88)',
      secondaryText: 'rgba(255, 255, 255, 0.5)',
      tertiaryText: 'rgba(255, 255, 255, 0.38)',
      navigationTintColor: '#B49CFF',
      accent: '#B49CFF',
      brandAvatarBackground: '#B49CFF',
      primaryButtonBackgroundDefault: '#C2A9FA',
      primaryButtonBackgroundHover: '#B49CFF',
      primaryButtonContent: '#161616',
      brandAvatarIcon: '#0D0D0D',
      sendFileButton: '#C2A9FA',
      messageInputBackground: '#393939',
      outgoingMessageBackground: '#C2A9FA',
      outgoingMessageText: 'rgba(0, 0, 0, 0.88)',
      incomingMessageBackground: '#393939',
      incomingMessageText: 'rgba(255, 255, 255, 0.88)',
      suggestedReplyBackgroundDefault: '#7129E5',
      suggestedReplyBackgroundHover: '#4B11A1',
      suggestedReplyText: 'white',
      readIconColor: '#69C085',
      defaultFaqItemColor: '#B49CFF',
      defaultFaqItemContent: '#0D0D0D',
    },
  })
);

export type InboxDemoTheme = typeof defaultTheme;

declare module 'dripsy' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DripsyCustomTheme extends InboxDemoTheme {}
}
